import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import classnames from 'classnames';

import { scrollMapIntoViewport, getColorsBySteps, getColorsByData } from '~/core/scripts/map-components/utils';
import BasisMap from '~/core/scripts/map-components/BasisMap';
import Choropleth from '~/core/scripts/map-components/ChoroplethLayer';
import OverlayFiltered from '~/core/scripts/map-components/OverlayLayerFiltered';
import Overlay from '~/core/scripts/map-components/OverlayLayer';
import RankingMarker from '~/core/scripts/map-components/RankingMarker';
import GeocoderMarker from '~/core/scripts/map-components/GeocoderMarker'
import SearchMarker from '~/core/scripts/map-components/SearchMarker';
import Icon from '~/core/scripts/components/Icon';

import { is768Query } from '~/core/scripts/mediaquery-helper';
import Geocoder from '~/core/scripts/components/Geocoder';
import Legend from '~/core/scripts/components/Legend';
import Tooltip from '~/core/scripts/components/Tooltip';
import Portal from '~/core/scripts/components/Portal';

import './Map.styl';

function propError(propName, componentName) {
  return new Error(
    `Invalid prop ${propName} supplied to ${componentName}.
    You need to pass choroplethSource OR choroplethData.`
  );
}

class MapWrapper extends PureComponent {
  static propTypes = {
    choroplethSource: (props, propName, componentName) => {
      if (!props.choroplethData) {
        if (typeof props[propName] !== 'string') {
          propError(propName, componentName);
        }
      }
    },
    choroplethData: (props, propName, componentName) => {
      if (!props.choroplethSource) {
        if (typeof props[propName] !== 'object') {
          propError(propName, componentName);
        }
      }
    },
    colorKey: PropTypes.string,
    valueKey: PropTypes.string,
    colorSteps: PropTypes.arrayOf(PropTypes.array),
    showLegend: PropTypes.bool,
    legendPortalSelector: PropTypes.string,
    showMapToggleButton: PropTypes.bool,
    zoomDelta: PropTypes.number,
    zoomSnap: PropTypes.number,
    zoomAnimation: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    searchPresets: PropTypes.array,
    searchPresetsTitle: PropTypes.string,
    smoothFactor: PropTypes.number,
    hasTooltip: PropTypes.bool
  }

  static defaultProps = {
    colorKey: null,
    valueKey: null,
    colorSteps: null,
    showGeocoder: false,
    showLegend: false,
    legendPortalSelector: null,
    showMapToggleButton: true,
    zoomDelta: 1,
    zoomSnap: 0.2,
    zoomAnimation: true,
    isFullScreen: false,
    searchPresets: [],
    smoothFactor: 0.5,
    hasTooltip: true,
    onClick: null
  }

  state = {
    data: null,
    mapBounds: null,
    tooltipPos: { x: 0, y: 0 },
    tooltipData: null,
    tooltipVisible: false,
    geocoderLatLng: [],
    overlayGeoJson: null,
    maxBounds: null,
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    const data = this.props.choroplethData;
    const mapBounds = L.geoJSON(data).getBounds();
    const maxBounds = mapBounds.pad(0.2);

    this.setState({ data, mapBounds, maxBounds, overlayGeoJson: data });
  }

  onCloseTooltip = () => {
    this.setState({ tooltipVisible: false });
  }

  onGeocoderSuccess = (res) => {
    const latlng = res.response.view[0].result[0].location.displayPosition;
    this.setState({
      geocoderLatLng: [
        latlng.latitude + Math.random() / 10000,
        latlng.longitude + Math.random() / 10000
      ]
    });

    if (!is768Query()) {
      scrollMapIntoViewport();

      // for mobile devices we want to show the tooltip
      let matchingFeature = null;

      for (let i = 0; i < this.state.data.features.length; i++) {
        const currFeat = this.state.data.features[i];
        const isInside = currFeat.type && currFeat.geometry && booleanPointInPolygon([latlng.longitude, latlng.latitude], currFeat);
        if (isInside) {
          matchingFeature = currFeat;
          break;
        }
      }

      this.onMouseEnter(matchingFeature.properties);
    }
  }

  onMouseEnter = (tooltipData) => {
    this.setState({
      tooltipVisible: true,
      tooltipData: {
        value: tooltipData[this.props.valueKey],
        valueAbs: tooltipData[`${this.props.valueKey}_abs`],
        valueGreen: tooltipData.Gruene,
        title: tooltipData.Kreis,
        subtitle: tooltipData.Bundesland,
        info: tooltipData.info,
        color: this.props.colorizer(tooltipData)
      }
    });
  }

  onMouseMove = (tooltipPos) => {
    this.setState({ tooltipPos });
  }

  onMouseOut = () => {
    this.setState({ tooltipVisible: false });
  }

  renderLegend() {
    if (!this.props.showLegend) {
      return null;
    }

    const legendColors = this.props.colorSteps ?
      getColorsBySteps(this.props.colorSteps) :
      getColorsByData({ data: this.state.data, colorKey: this.props.colorKey, valueKey: this.props.valueKey });

    const LegendComp = (
      <Legend
        isVisible={!!this.state.data}
        minLabel={this.props.legendMinLabel}
        maxLabel={this.props.legendMaxLabel}
        colors={legendColors}
      />
    );

    if (this.props.legendPortalSelector) {
      return (
        <Portal selector={this.props.legendPortalSelector}>
          {LegendComp}
        </Portal>
      );
    }

    return LegendComp;
  }

  renderButton() {
    if (!this.props.hasTooltip) {
      return null;
    }
    const { sectionIndex, filterUsed } = this.props;

    return (
      <div className="section__filter">
        <button
          className="btn greenglasses"
          onClick={() => this.props.onFilter(this.btn)}
          ref={(ref) => { this.btn = ref; }}
        >
            <Icon type="glasses" />
            <span className="btn__label">Grüne Brille</span>
            {(sectionIndex === 1 && !filterUsed) && (
              <div className="section__swoopy">
                <div className="section__swoopy-label">
                  Nur Kreise anzeigen, in denen die <strong>Grünen</strong> ein <strong>überdurchschnittliches Ergebnis</strong> hatten.
                </div>
                <Icon type="arrow-curved" />
              </div>
            )}
        </button>
      </div>
    );
  }

  render() {
    const mapClasses = classnames('map-widget', this.props.className);

    return (
      <div className={mapClasses}>
        {this.renderLegend()}
        {this.renderButton()}
        <div className="map-wrapper">
          <BasisMap
            minZoom={this.props.minZoom}
            maxZoom={this.props.maxZoom}
            maxBounds={this.state.maxBounds}
            fitBounds={this.state.mapBounds}
            showBasemap={this.props.showBasemap}
            basemapUrl={this.props.basemapUrl}
            basemapAttribution={this.props.basemapAttribution}
            geocoderLatLng={this.state.geocoderLatLng}
            zoomControl={this.props.zoomControl}
            showMapToggleButton={this.props.showMapToggleButton}
            showLabels={this.props.showLabels}
            labelPreset={this.props.labelPreset}
            customLabels={this.props.customLabels}
            zoomDelta={this.props.zoomDelta}
            zoomSnap={this.props.zoomSnap}
            zoomAnimation={this.props.zoomAnimation}
            isFullScreen={this.props.isFullScreen}
            isFiltered={this.props.isFiltered}
          >
            <Choropleth
              data={this.props.choroplethData}
              colorizer={this.props.colorizer}
              onMouseEnter={this.onMouseEnter}
              onMouseMove={this.onMouseMove}
              onMouseOut={this.onMouseOut}
              onClick={this.props.onClick}
              className="mapwidget__choropleth"
              smoothFactor={this.props.smoothFactor}
              filterFunc={this.props.filterFunc}
              isFiltered={this.props.isFiltered}
              isVisible={this.props.isVisible}
              mapSource={this.props.mapSource}
            />
            <OverlayFiltered
              data={this.props.filteredGeojson}
              className="mapwidget__overlay filtered"
              smoothFactor={this.props.smoothFactor}
              filterFunc={this.props.filterFunc}
              isFiltered={this.props.isFiltered}
            />
            <Overlay
              data={this.props.stateGeojson}
              dissolveKey="Bundesland"
              className="mapwidget__overlay"
              smoothFactor={this.props.smoothFactor}
              isFiltered={this.props.isFiltered}
            />
            <RankingMarker
              data={this.props.data}
              geojson={this.props.choroplethData}
              isFiltered={this.props.isFiltered}
              valueKey={this.props.valueKey}
              inverted={this.props.inverted}
            />
            <SearchMarker data={this.props.selectedArea} />
            <GeocoderMarker
              position={this.state.geocoderLatLng}
            />
          </BasisMap>
          {this.props.hasTooltip && (
            <Tooltip
              isVisible={this.state.tooltipVisible}
              pos={this.state.tooltipPos}
              data={this.state.tooltipData}
              onClose={this.onCloseTooltip}
              tooltipAbsLabel={this.props.tooltipAbsLabel}
              icon={this.props.icon}
              decimals={this.props.decimals}
              unit={this.props.unit}
            />
          )}
        </div>
        {this.props.showGeocoder && (
          <Geocoder
            title="Wie ist es bei Ihnen?"
            onSelect={this.onGeocoderSuccess}
            presets={this.props.searchPresets}
            presetsTitle={this.props.searchPresetsTitle}
          />
        )}
      </div>
    );
  }
}

export default MapWrapper;
